import { Theme } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { IntlShape } from "react-intl";
import { AppearanceTheme, ColorSlot, COLOR_SLOTS, MessageContent, TApplicationUIConfigForm, ThemeColors } from "./uiConfig.types";

// Human-readable label + tooltip description per slot (default messages; the intl ids are
// `branding.slot.label.<slug>` and `branding.slot.description.<slug>`, see SLOT_MESSAGE_SLUG).
// Single source for the editor, FE-App and FE-Preview.
export const SLOT_META: Record<ColorSlot, { label: string; description: string }> = {
    primaryActionBg: { label: "Primary button", description: "Fill of the main buttons: Sign In, Continue, Accept & Confirm." },
    primaryActionText: { label: "Primary button text", description: "Text inside the main buttons (Sign In, Continue)." },
    secondaryActionBg: { label: "Secondary button", description: "Fill of the secondary buttons: Google/Microsoft sign-in, Create Account." },
    secondaryActionText: { label: "Secondary button text", description: "Text inside the secondary buttons." },
    secondaryActionBorder: { label: "Secondary button border", description: "Border around the secondary buttons." },
    links: { label: "Links", description: "Link text: Forgot Password, Create Account, Read Terms, Back." },
    bodyText: { label: "Body text", description: "Page headings and typed input text — e.g. the “Sign In” title." },
    secondaryText: { label: "Secondary text", description: "Subtitles, captions and helper text under the headings." },
    formBackground: { label: "Form background", description: "Background of the form area, behind the fields." },
    inputBorder: { label: "Input border", description: "Outline of input fields and the verification-code boxes." },
    disabledButtonBg: { label: "Disabled button", description: "Fill of a button while disabled (e.g. before the form is complete)." },
    errorColor: { label: "Error", description: "Validation errors: the message and the invalid field’s outline." },
    successColor: { label: "Success", description: "Success states, e.g. the confirmation check icon." },
};

/**
 * Slot name → the kebab-case segment its message ids use (NG-8799).
 *
 * The slot names themselves are camelCase and stay that way: they are keys inside the
 * `configuration` JSON blob persisted per tenant (`IUiCustomizationRecord.configuration`), so
 * renaming them would mean migrating stored branding configs, not just editing this file.
 * The ids are ours alone, so they follow the `docs/i18n-conventions.md` §1 kebab convention.
 *
 * Written out rather than derived with a camel-to-kebab helper on purpose: a literal table keeps
 * every key greppable from the catalog back to the slot, which is the property NG-8614 was about.
 *
 * `as const satisfies` rather than a `Record<ColorSlot, string>` annotation, and the difference is
 * load-bearing: the annotation widens every slug to `string`, which degrades the call site's id to
 * `branding.slot.label.${string}` and fails `npm run type-check:intl` against the catalog union.
 * `satisfies` keeps the completeness check — a new slot still cannot be added without a slug —
 * while the literal types let the id resolve to the 13 concrete keys that exist.
 */
export const SLOT_MESSAGE_SLUG = {
    primaryActionBg: "primary-action-bg",
    primaryActionText: "primary-action-text",
    secondaryActionBg: "secondary-action-bg",
    secondaryActionText: "secondary-action-text",
    secondaryActionBorder: "secondary-action-border",
    links: "links",
    bodyText: "body-text",
    secondaryText: "secondary-text",
    formBackground: "form-background",
    inputBorder: "input-border",
    disabledButtonBg: "disabled-button-bg",
    errorColor: "error-color",
    successColor: "success-color",
} as const satisfies Record<ColorSlot, string>;

// Single source of truth: each branding color slot → the live theme token it defaults to.
// Resolvers read from the theme so defaults track the palette (never copied hex literals).
// Custom palette keys are optional in the augmented types, hence the `?.` + "" fallback.
export const SLOT_TOKEN_RESOLVERS: Record<ColorSlot, (theme: Theme) => string> = {
    primaryActionBg: (t) => t.palette.merigold?.main ?? "",
    // contrastText, not softBlack: the fill stays Amber in dark mode, so its text must stay Charcoal
    primaryActionText: (t) => t.palette.merigold?.contrastText ?? "",
    // paper, not common.white: the secondary buttons must flip with the color mode
    secondaryActionBg: (t) => t.palette.background.paper,
    secondaryActionText: (t) => t.palette.softBlack?.main ?? "",
    secondaryActionBorder: (t) => t.palette.merigold?.main ?? "",
    links: (t) => t.palette.teal?.main ?? "",
    bodyText: (t) => t.palette.text.primary,
    secondaryText: (t) => t.palette.mediumDarkGrey?.main ?? "",
    formBackground: (t) => t.palette.background.default,
    inputBorder: (t) => t.palette.silverGrey?.light ?? "",
    disabledButtonBg: (t) => t.palette.silver?.main ?? "",
    errorColor: (t) => t.palette.error.main,
    successColor: (t) => t.palette.success.main,
};

// Resolve every slot's default from the given theme.
export const resolveThemeColors = (theme: Theme): ThemeColors =>
    COLOR_SLOTS.reduce((acc, slot) => ({ ...acc, [slot]: SLOT_TOKEN_RESOLVERS[slot](theme) }), {} as ThemeColors);

// Dark variant of the live theme: keep the brand/custom tokens (mode-independent) but let MUI
// supply its dark defaults for the mode-dependent standard channels (background, text, divider,
// action). In practice this flips formBackground + bodyText for dark while everything else stays.
const buildDarkTheme = (theme: Theme): Theme =>
    createTheme({
        palette: {
            mode: "dark",
            error: { main: theme.palette.error.main },
            success: { main: theme.palette.success.main },
            merigold: theme.palette.merigold,
            teal: theme.palette.teal,
            softBlack: theme.palette.softBlack,
            silverGrey: theme.palette.silverGrey,
            silver: theme.palette.silver,
            mediumDarkGrey: theme.palette.mediumDarkGrey,
        },
    });

// Seed content with what the Login/Register pages currently show: Login has only a heading today;
// Register defaults to a title + subtitle. Descriptions are new to branding, so left empty.
export const buildSeededContent = (formatMessage: IntlShape["formatMessage"]): { login: MessageContent; registration: MessageContent } => ({
    login: {
        title: formatMessage({ id: "common.button-label.sign-in", defaultMessage: "Sign In" }),
        subtitle: "",
        description: "",
    },
    registration: {
        title: formatMessage({ id: "register-v3.title.email", defaultMessage: "Create Account" }),
        subtitle: formatMessage({ id: "register-v3.subtitle.email", defaultMessage: "Enter your details to get started" }),
        description: "",
    },
});

// Full default form pre-filled from the theme (colors) and the app's current copy (content): light
// colors from the live theme, dark from a MUI dark-mode variant of it. Each mode gets its own object.
export const buildSeededDefaults = (theme: Theme, formatMessage: IntlShape["formatMessage"]): TApplicationUIConfigForm => ({
    isEnabled: true,
    defaultMode: AppearanceTheme.SYSTEM,
    content: buildSeededContent(formatMessage),
    colors: { light: resolveThemeColors(theme), dark: resolveThemeColors(buildDarkTheme(theme)) },
});
